import { type CountryKey, countryDialCodes } from '@mnd-frontend/ui/shared/lists/countries';
import {
  BREAKPOINTS,
  RADIUS_XL,
  WEB_FONT_FORM_FIELD_INPUT,
  WEB_GREY_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { type ChangeEvent, type InputHTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { countryOptions } from '../../utils/formatters/countries';
import { type FieldNames, FORM_FIELD } from '../Form/index.type';
import Input from '../Input';
import Select from '../Select';

const PhoneFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 5rem auto;
  gap: 0.5rem;

  @media (min-width: ${BREAKPOINTS.XS}px) {
    grid-template-columns: 8rem auto;
  }
`;

const DialInputWrapper = styled.div`
  padding: 0.5rem 1rem;
  font-size: ${WEB_FONT_FORM_FIELD_INPUT};
  line-height: 150%;
  border-radius: ${RADIUS_XL};
  background: rgba(0, 0, 0, 0.05);
  height: fit-content;
  display: flex;
  align-items: center;

  &::before {
    content: '+';
    width: 0.5rem;
    margin-right: 0.5rem;
  }
`;

const StyledInput = styled.input<{ $isReadOnly?: boolean }>`
  border: 0;
  background: transparent;
  height: 100%;
  font-size: ${WEB_FONT_FORM_FIELD_INPUT};
  line-height: 150%;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${WEB_GREY_REG};
    font-size: inherit;
    font-weight: 400;
  }
`;

const InputReadOnly = ({ value, placeholder }: InputHTMLAttributes<HTMLInputElement>) => {
  return (
    <DialInputWrapper>
      <StyledInput
        id="dial-code"
        type="text"
        value={value}
        placeholder={placeholder}
        readOnly
        disabled
        $isReadOnly={true}
      />
    </DialInputWrapper>
  );
};

const InputCountryAndPhone = ({
  errors,
  validateHookPhone,
  validateHookCountry,
  label,
  countries,
  placeholders,
}: {
  errors: {} | Record<string, string>;
  label?: string;
  validateHookPhone?: (
    fieldName: FieldNames,
    data: {
      value: string;
      isRequired: boolean;
    },
  ) => void;
  validateHookCountry?: (
    fieldName: FieldNames,
    data: {
      value: string;
      isRequired: boolean;
    },
  ) => void;
  countries?: Array<{ label: string; value: string }>;
  placeholders?: { country?: string; phone?: string };
}) => {
  const { t } = useTranslation();
  const [countryCode, setCountryCode] = useState<CountryKey | ''>('');

  const getDialNumber = (code: CountryKey) => {
    if (code === null) return '';
    return countryDialCodes[code];
  };
  const [dialCode, setDialCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const onChangeCountry = (e: ChangeEvent<HTMLSelectElement>) => {
    const country = e.currentTarget.value as CountryKey;
    setCountryCode(country);
    country ? setDialCode(getDialNumber(country)) : setDialCode('');
  };

  const COUNTRY_OPTIONS = countryOptions();
  const fieldError = {
    country: FORM_FIELD.COUNTRY in errors ? errors[FORM_FIELD.COUNTRY] : undefined,
    phone: FORM_FIELD.PHONE in errors ? errors[FORM_FIELD.PHONE] : undefined,
  };

  return (
    <>
      <Select
        name={FORM_FIELD.COUNTRY}
        label={label ?? t('form.field.label.phone')}
        placeholder={placeholders?.country ?? t('form.field.placeholder.countryPhoneCode')}
        onChange={onChangeCountry}
        onBlur={e => {
          validateHookCountry?.(FORM_FIELD.COUNTRY, { value: e.target.value, isRequired: true });
        }}
        value={countryCode}
        options={countries ?? (COUNTRY_OPTIONS as Array<{ label: string; value: string }>)}
        error={fieldError.country}
        required
      />

      <div>
        <PhoneFieldGrid>
          <InputReadOnly value={dialCode} placeholder="--" />
          <Input
            type="tel"
            id="phone-with-code"
            placeholder={placeholders?.phone ?? t('form.phone')}
            onChange={e => setPhoneNumber(e.currentTarget.value)}
            onBlur={e =>
              validateHookPhone?.(FORM_FIELD.PHONE, {
                value: e.currentTarget.value,
                isRequired: true,
              })
            }
            error={fieldError.phone}
            required
          />
          <input
            hidden
            name={FORM_FIELD.PHONE}
            value={`${dialCode} ${phoneNumber}`}
            readOnly
            aria-hidden
          />
        </PhoneFieldGrid>
      </div>
    </>
  );
};

export default InputCountryAndPhone;
